import { useState, useEffect } from 'react';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Navbar({ theme, toggleTheme }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const isBranding = location.pathname === '/branding';
  const { currentUser, isAdmin, isInstructor } = useAuth();

  const dashboardLink = isAdmin ? '/admin/overview' : isInstructor ? '/instructor-dashboard' : '/dashboard';

  // ── Route-conditional nav links ──
  const homeLinks = [
    { name: 'الدورات', href: '/courses', isRoute: true },
    { name: 'أعمال مبدعينا', href: '/creators', isRoute: true },
    { name: 'صناع الإلهام', href: '/inspirers', isRoute: true },
    { name: 'تعرف علينا', href: '/about', isRoute: true },
    { name: 'براندينج', href: '/branding', isRoute: true },
    { name: 'المدونة', href: '/blog', isRoute: true },
  ];

  const brandingLinks = [
    { name: 'خطة الدورة', href: isBranding ? '#roadmap' : '/#roadmap' },
    { name: 'محتوى الدورة', href: isBranding ? '#course-content' : '/#course-content' },
    { name: 'المميزات', href: isBranding ? '#features' : '/#features' },
    { name: 'تعرف على المدرب', href: isBranding ? '#mentor' : '/#mentor' },
    { name: 'الرسوم', href: isBranding ? '#pricing' : '/#pricing' },
    { name: 'الأسئلة الشائعة', href: isBranding ? '#faq' : '/#faq' },
    { name: 'الدفع والاشتراك', href: isBranding ? '#PaymentMethods' : '/#PaymentMethods' },
  ];

  const navLinks = isBranding ? brandingLinks : homeLinks;

  // ── Scroll tracking (for section highlighting on branding page) ──
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      if (!isBranding) return;
      const sections = brandingLinks.map(l => l.href.split('#')[1]).filter(Boolean);
      let current = '';
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 250 && rect.bottom > 250) { current = section; break; }
        }
      }
      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isBranding]);

  // ── Show theme toggle + auth only on non-branding pages ──
  const showControls = !isBranding;

  return (
    <header className={`relative w-full z-50 transition-all duration-500 ${scrolled
      ? 'bg-white/80 dark:bg-zinc-950/80 backdrop-blur-2xl shadow-sm border-b border-slate-200/50 dark:border-white/5'
      : 'bg-white/50 dark:bg-zinc-950/50 backdrop-blur-xl border-b border-transparent'
      }`}>
      <div className="container mx-auto px-4 md:px-6 h-20 flex items-center justify-between">
        <Link to="/" onClick={() => isHome && window.scrollTo(0, 0)} className="flex items-center gap-3 group cursor-pointer">
          <img src="/lobab.png" alt="Lobab School" className="text-5xl font-bold text-[#e60024] dark:text-primary-400 transition-colors h-14" />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => {
            // Route-based links (homepage)
            if (link.isRoute) {
              const isActiveRoute = location.pathname.startsWith(link.href);
              return (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`relative py-1 text-xl font-bold transition-colors ${
                    isActiveRoute
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-slate-600 dark:text-zinc-300 hover:text-primary-600 dark:hover:text-primary-400'
                  } after:content-[''] after:absolute after:-bottom-1 after:right-0 after:h-0.5 after:bg-primary-600 dark:after:bg-primary-400 after:transition-all after:duration-300 ${
                    isActiveRoute ? 'after:w-full' : 'after:w-0 hover:after:w-full'
                  }`}
                >
                  {link.name}
                </Link>
              );
            }
            // Hash-based links (branding page sections)
            const isActiveHash = activeSection === (link.href || '').split('#')[1];
            return (
              <a
                key={link.name}
                href={link.href}
                className={`relative py-1 text-lg font-bold transition-colors ${
                  isActiveHash
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-slate-600 dark:text-zinc-300 hover:text-primary-600 dark:hover:text-primary-400'
                } after:content-[''] after:absolute after:-bottom-1 after:right-0 after:h-0.5 after:bg-primary-600 dark:after:bg-primary-400 after:transition-all after:duration-300 ${
                  isActiveHash ? 'after:w-full' : 'after:w-0 hover:after:w-full'
                }`}
              >
                {link.name}
              </a>
            );
          })}

          {/* Controls: Theme toggle + Auth (hidden on branding page) */}
          {showControls && (
            <>
              <button
                onClick={toggleTheme}
                className="relative w-16 h-8 flex items-center bg-slate-200 dark:bg-zinc-800 rounded-full p-1 transition-all duration-500 overflow-hidden shadow-inner border border-slate-300 dark:border-white/10 hover:border-primary-500/50"
                aria-label="Toggle Dark Mode"
              >
                <div className={`absolute w-6 h-6 bg-white dark:bg-zinc-950 rounded-full shadow-md transform transition-transform duration-500 flex items-center justify-center z-10 ${theme === 'dark' ? 'translate-x-[2rem]' : 'translate-x-[0]'}`}>
                  {theme === 'dark' ? <Moon className="w-3.5 h-3.5 text-primary-500" /> : <Sun className="w-3.5 h-3.5 text-primary-500" />}
                </div>
                <div className="w-full flex justify-between px-1.5 opacity-50 text-slate-500 dark:text-zinc-400">
                  <Moon className="w-3.5 h-3.5" />
                  <Sun className="w-3.5 h-3.5" />
                </div>
              </button>
              <Link to="/instructor-signup" className=" lg:flex px-5 py-2.5 rounded-xl bg-amber-600 text-white text-md font-bold transition-all hover:bg-amber-500 hover:-translate-y-0.5 shadow-md shadow-amber-500/25">
                انظم كمدرب
              </Link>
              {currentUser ? (
                <Link to={dashboardLink} className="hidden lg:flex px-5 py-2.5 rounded-xl bg-primary-600 text-white text-md font-bold transition-all hover:bg-primary-500 hover:-translate-y-0.5 shadow-md shadow-primary-500/25">
                  لوحة التحكم
                </Link>
              ) : (
                <Link to="/login" className="hidden lg:flex px-5 py-2.5 rounded-xl bg-primary-600 text-white text-md font-bold transition-all hover:bg-primary-500 hover:-translate-y-0.5 shadow-md shadow-primary-500/25">
                  تسجيل الدخول
                </Link>
              )}
            </>
          )}
        </nav>

        {/* Mobile Toggle Icons */}
        <div className="md:hidden flex items-center gap-3">
          {showControls && (
            <button
              onClick={toggleTheme}
              className="w-10 h-10 flex items-center justify-center bg-slate-100 dark:bg-white/5 rounded-xl border border-slate-200 dark:border-white/10 text-primary-500"
              aria-label={theme === 'dark' ? 'تفعيل الوضع الفاتح' : 'تفعيل الوضع الداكن'}
            >
              {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </button>
          )}

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="w-10 h-10 flex items-center justify-center bg-slate-100 dark:bg-white/5 rounded-xl border border-slate-200 dark:border-white/10 text-slate-600 dark:text-zinc-300"
            aria-label={isMenuOpen ? 'إغلاق القائمة' : 'فتح القائمة'}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div className={`md:hidden absolute top-full left-0 right-0 h-[calc(100vh-5rem)] bg-white/98 dark:bg-zinc-950/98 backdrop-blur-2xl z-[60] transition-all duration-300 transform ${isMenuOpen ? 'translate-y-0 opacity-100 pointer-events-auto' : '-translate-y-full opacity-0 pointer-events-none'} border-b border-slate-200 dark:border-white/10 overflow-hidden`} dir="rtl">
        <div className="flex flex-col bg-white dark:bg-zinc-950 p-8 gap-6 overflow-y-auto h-[calc(100vh-5rem)]">
          {navLinks.map((link) => {
            if (link.isRoute) {
              const isActiveRoute = location.pathname.startsWith(link.href);
              return (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={`relative w-fit text-2xl font-extrabold pb-2 transition-colors ${
                    isActiveRoute
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-slate-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400'
                  } after:content-[''] after:absolute after:bottom-0 after:right-0 after:h-0.5 after:bg-primary-600 dark:after:bg-primary-400 after:transition-all after:duration-300 ${
                    isActiveRoute ? 'after:w-full' : 'after:w-0 hover:after:w-full'
                  }`}
                >
                  {link.name}
                </Link>
              );
            }
            const isActiveHash = activeSection === (link.href || '').split('#')[1];
            return (
              <a
                key={link.name}
                href={link.href}
                onClick={() => setIsMenuOpen(false)}
                className={`relative w-fit text-2xl font-extrabold pb-2 transition-colors ${
                  isActiveHash
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-slate-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400'
                } after:content-[''] after:absolute after:bottom-0 after:right-0 after:h-0.5 after:bg-primary-600 dark:after:bg-primary-400 after:transition-all after:duration-300 ${
                  isActiveHash ? 'after:w-full' : 'after:w-0 hover:after:w-full'
                }`}
              >
                {link.name}
              </a>
            );
          })}
          {showControls && (
            <div className="mt-6 pb-12">
              <Link
                to={currentUser ? dashboardLink : '/login'}
                onClick={() => setIsMenuOpen(false)}
                className="block w-full py-5 text-center rounded-2xl bg-primary-600 text-white text-xl font-extrabold shadow-xl hover:bg-primary-500 transition-all active:scale-95"
              >
                {currentUser ? 'لوحة التحكم' : 'تسجيل الدخول'}
              </Link>
              {!currentUser && (
                <Link
                  to="/instructor-signup"
                  onClick={() => setIsMenuOpen(false)}
                  className="block w-full mt-3 py-4 text-center rounded-2xl border-2 border-amber-200 dark:border-amber-500/20 text-amber-600 dark:text-amber-400 text-xl font-extrabold hover:bg-amber-50 dark:hover:bg-amber-500/10 transition-all active:scale-95"
                >
                  <span className="text-2xl">انضم كمدرب</span>
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
